import React from 'react';
import { currentPreviousTooltip } from './currentPreviousTooltip';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { MUTED_PALETTE, BandGradient, labBandFill, pastCeilingFill } from './chartPalette';
import { bloodDropLabel, BLOOD_DROP_EDGE_OPTIONS } from './bloodDropLabel';

const bloodDrop = require('../../assets/imgs/blood_drop_black.png');
const bloodDropPrevious = require('../../assets/imgs/blood_drop_blue_50.png');

/**
 * Symmetric reference-range bar for the MVX "optimal-in-the-middle" factors
 * (citrate + the three BCAAs). Mirrors the app's standard ReferenceRangeChart
 * (default layout) exactly: green optimal centered on a normalized −133.3…+133.3
 * axis, with Lab (yellow) / > Lab (orange) / Alarm (red) shoulders mirrored on
 * BOTH sides, band names centered above, boundary values labeled below, and a
 * blood-drop marker mapped piecewise into its zone. Each Lab band leans green
 * at the edge that touches Optimal; see labBandFill in chartPalette.
 */
const { green: GREEN, orange: ORANGE, red: RED } = MUTED_PALETTE;
const GRID_LINE_COLOR = '#CDD7EA';

/**
 * Extra pixels above a labelled window bar, so its band names clear the
 * row of numbers under the bar above them. 8 reads as a gap rather than a
 * near miss, and costs one row of the panel nothing anyone notices.
 */
const LABEL_HEADROOM = 8;
/*
 * NOTHING NAMES THE SHARED BOUND, and that is deliberate.
 *
 * Small HDL-P's optimal and lab ceilings are both 21.6, so the usual yellow
 * "lab-normal but above optimal" band does not exist. A "Lab max" label was
 * tried in that gap on 2026-10-15 and taken straight back out: it landed
 * beside "> Lab" and the pair read as one phrase, "Lab max > Lab", which
 * says nothing. "Optimal / Lab" on the green was considered and rejected
 * too, because the green is the TOP of the lab range and the yellow to its
 * left is lab-normal as well.
 *
 * The row reads correctly without help: Alarm, < Lab, Lab, Optimal, > Lab,
 * Alarm. The absent band is only noticeable against a bar that has one, and
 * the Range References tab explains the shared ceiling in words for anyone
 * who goes looking.
 */

/**
 * The band names are a LEGEND, not a heading.
 *
 * They were bold #333: heavier than the tick numbers below them and as loud
 * as the marker names beside them, which inverted the hierarchy. What should
 * carry a reader through this panel is the marker, its value and the drop;
 * the band names are read once and then stop being looked at. With five of
 * the nine rows now leading a group, that weight made the whole section
 * read as busy (Maryann, 2026-10-15).
 *
 * Regular weight in a warm gray that still clears AA at this size: 5.36:1 on
 * white, against 3.74:1 for the secondary gray used in the Insights panel,
 * which is too light for 10px type.
 */
const LABEL_COLOR = '#6F6A64';

/** Matches MvxSeverityBar: the two bar types share one label treatment. */
const LABEL_STYLE = { color: LABEL_COLOR, fontSize: '10px', fontWeight: 'normal' };

const AXIS_MIN = -133.3;
const AXIS_MAX = 133.3;

export type MvxOptimalBarProps = {
  optimal: [number, number];
  reference: [number, number];
  severe: [number, number];
  value: number;
  /** The previous test's value; draws the blue comparison drop beside the black one. */
  previousValue?: number;
  /** Collection dates for the hover box, already formatted. */
  currentDate?: string;
  previousDate?: string;
  format: (n: number) => string;
  /** Show the band-name labels above the bar (usually only the first such row). */
  showLabels?: boolean;
};

const reflowSoon = (chart: Highcharts.Chart): void => {
  setTimeout(() => {
    try {
      chart.reflow();
    } catch (e) {
      /* chart unmounted */
    }
  }, 0);
};

export class MvxOptimalBar extends React.Component<MvxOptimalBarProps> {
  // Anchor value-space boundaries to the normalized axis, mirrored around the
  // optimal midpoint (0). severe→±100, reference→±66.6, optimal→±33.3.
  private anchors(): Array<[number, number]> {
    const [optLo, optHi] = this.props.optimal;
    const [refLo, refHi] = this.props.reference;
    const [sevLo, sevHi] = this.props.severe;
    const mean = (optLo + optHi) / 2;
    const all: Array<[number, number]> = [
      [sevLo, -100],
      [refLo, -66.6],
      [optLo, -33.3],
      [mean, 0],
      [optHi, 33.3],
      [refHi, 66.6],
      [sevHi, 100],
    ];
    // TWO BOUNDS ON ONE NUMBER GET ONE POSITION.
    //
    // Small HDL-P's optimal and lab ceilings are both 21.6, which anchored
    // that value at 33.3 AND at 66.6. The drop interpolated to the first and
    // the tick printed at the second, so a result of exactly 21.6 drew about
    // 76px to the left of its own label (Maryann spotted the gap,
    // 2026-10-15). Dropping the inner anchor leaves one position per value,
    // and the stretch from the mean to the ceiling then spans two slots
    // evenly instead of one slot plus a dead one.
    //
    // It is always the INNER anchor that goes, the one nearer the mean, so
    // the value keeps the outer position where its tick is printed. A filter
    // on "same as the previous entry" would drop the outer one on the high
    // side and the inner one on the low side, which is why the sides are
    // spelled out.
    const drop = new Set<number>();
    if (optHi === refHi) drop.add(4); // optHi at 33.3
    if (refHi === sevHi) drop.add(5); // refHi at 66.6
    if (optLo === refLo) drop.add(2); // optLo at -33.3
    if (refLo === sevLo) drop.add(1); // refLo at -66.6
    return all.filter((_, i) => !drop.has(i));
  }

  // value → normalized position, piecewise-linear across the anchors, clamped
  // into the outer (red) bands so an off-range value stays on-scale.
  private toNorm(value: number): number {
    const a = this.anchors();
    if (value <= a[0][0]) {
      const [v0, n0] = a[0];
      const [v1, n1] = a[1];
      return Math.max(AXIS_MIN, n0 + ((value - v0) * (n1 - n0)) / (v1 - v0));
    }
    const last = a.length - 1;
    if (value >= a[last][0]) {
      const [v0, n0] = a[last - 1];
      const [v1, n1] = a[last];
      return Math.min(AXIS_MAX, n1 + ((value - v1) * (n1 - n0)) / (v1 - v0));
    }
    for (let i = 0; i < last; i++) {
      const [v0, n0] = a[i];
      const [v1, n1] = a[i + 1];
      if (value <= v1) return n0 + ((value - v0) * (n1 - n0)) / (v1 - v0);
    }
    return 0;
  }

  render() {
    const { optimal, reference, severe, value, previousValue, format, showLabels, currentDate, previousDate } = this.props;
    const [optLo, optHi] = optimal;
    const [refLo, refHi] = reference;
    const [sevLo, sevHi] = severe;
    const normValue = this.toNorm(value);
    // -12 always, the house offset: with the label's 5px padding it centers the
    // 15px drop on its point (-12 + 5 + 7.5 = +0.5px). This USED to flip to +12
    // for points in the lower half, meant as an anti-clipping nudge, which
    // silently moved those drops ~24.5px to the RIGHT of the value they report —
    // visible on Small HDL-P, the one marker whose drop lands down there.
    // Clipping at the axis ends is BLOOD_DROP_EDGE_OPTIONS' job, as in RDW.
    const offset = -12;
    const normPrevious = previousValue === undefined ? undefined : this.toNorm(previousValue);

    // Boundary numbers below the bar. Position 0 is the MEAN — the midpoint of
    // the optimal band, which is exactly what the axis puts there, sitting
    // halfway between the optimalMin tick at -33.3 and the optimalMax tick at
    // +33.3. It is also the "Mean" column of the Master Ranges Sheet, so the
    // bar and the sheet report the same number. It used to render blank, which
    // left a conspicuous gap in the middle of every one of these bars.
    //
    // Only the two chart ends (±133.3) stay unlabeled: they are padding, not
    // bounds.
    const numByPos: { [k: string]: number } = {
      '-100': sevLo,
      '-66.6': refLo,
      '-33.3': optLo,
      '0': (optLo + optHi) / 2,
      '33.3': optHi,
      '66.6': refHi,
      '100': sevHi,
    };

    // TWO BOUNDS ON ONE NUMBER DRAW AS ONE BOUNDARY.
    //
    // Small HDL-P's optimal ceiling and lab ceiling are both Labcorp's 21.6,
    // because 21.6 is Dr. Schmidt's ceiling too. The interval between them is
    // empty: no result can land there. Printing "21.6  21.6" side by side
    // reads as a mistake, and suppressing one label while keeping its tick
    // leaves a tick with no number under it, which reads as missing data
    // (Maryann, 2026-10-15).
    //
    // So the inner bound is dropped outright: no label, no tick, and the band
    // it would have opened is painted as the band beyond it, since that is
    // what a value there would actually be. The anchors keep both positions,
    // because the bar's geometry interpolates between them.
    const collapsed = new Set<string>();
    ([['33.3', '66.6'], ['66.6', '100'], ['-33.3', '-66.6'], ['-66.6', '-100']] as const).forEach(
      ([inner, outer]) => {
        if (numByPos[inner] === numByPos[outer]) collapsed.add(inner);
      },
    );
    // The freed slot carries a REAL reading, the midpoint between the mean
    // and the shared bound, rather than a second copy of that bound or a
    // tick with nothing under it (Maryann, 2026-10-15). With the anchors
    // collapsed above, that is exactly what sits at this position.
    const mid = (a: number, b: number) => Math.round(((a + b) / 2) * 100) / 100;
    if (collapsed.has('33.3')) numByPos['33.3'] = mid(numByPos['0'], numByPos['66.6']);
    if (collapsed.has('-33.3')) numByPos['-33.3'] = mid(numByPos['0'], numByPos['-66.6']);

    const band = (from: number, to: number, color: string | BandGradient, text: string): Highcharts.YAxisPlotBandsOptions => ({
      from,
      to,
      color,
      label: showLabels
        ? { text, align: 'center', verticalAlign: 'top', y: -5, style: LABEL_STYLE }
        : undefined,
    });

    const options: Highcharts.Options = {
      // A LABELLED WINDOW BAR IS TALLER, by LABEL_HEADROOM.
      //
      // The band names sit above the plot, and on these rows the thing
      // directly above them is the previous bar's row of numbers: Small
      // HDL-P sits under GlycA's, citrate under MMX's. At the shared
      // geometry the two rows of small text very nearly touch (Maryann,
      // 2026-10-16). The score bars do not have the problem, because their
      // labelled rows open a group and already clear the hairline above.
      //
      // The plot itself is unchanged, so the bar, its ticks and its numbers
      // all stay put: the extra pixels go above the label and nowhere else.
      chart: {
        type: 'bar',
        height: 60 + (showLabels ? LABEL_HEADROOM : 0),
        marginTop: 20 + (showLabels ? LABEL_HEADROOM : 0),
        marginBottom: 20,
        backgroundColor: 'transparent',
      },
      title: { text: '' },
      xAxis: [{ labels: { enabled: false }, tickLength: 0 }],
      yAxis: {
        title: { text: '' },
        min: AXIS_MIN,
        max: AXIS_MAX,
        tickPositions: [-133.3, -100, -66.6, -33.3, 0, 33.3, 66.6, 100, 133.3],
        labels: {
          enabled: true,
          formatter: function () {
            const key = String((this as any).value);
            return key in numByPos ? format(numByPos[key]) : '';
          },
          style: { fontSize: '10px', color: '#555' },
        },
        gridLineWidth: 1,
        gridLineColor: GRID_LINE_COLOR,
        plotBands: [
          band(-133.3, -100, RED, 'Alarm'),
          band(-100, -66.6, collapsed.has('-66.6') ? pastCeilingFill('right') : ORANGE, '< Lab'),
          // A Lab band whose two bounds are the same number holds no values.
          // It is painted GREEN, because the optimal run reaches the one
          // number both bounds share and that number is labeled at the far
          // edge of this band: stopping the green at the near edge drew the
          // ceiling well to the right of where optimal appeared to end
          // (Maryann, 2026-10-15).
          band(-66.6, -33.3, collapsed.has('-66.6') ? GREEN : labBandFill('right'), collapsed.has('-66.6') ? '' : 'Lab'),
          band(-33.3, 33.3, GREEN, 'Optimal'),
          band(33.3, 66.6, collapsed.has('33.3') ? GREEN : labBandFill('left'), collapsed.has('33.3') ? '' : 'Lab'),
          // ...and the band past the ceiling ramps out of it rather than
          // stepping, since there is no Lab band left to do that job.
          band(66.6, 100, collapsed.has('33.3') ? pastCeilingFill('left') : ORANGE, '> Lab'),
          band(100, 133.3, RED, 'Alarm'),
        ],
      },
      // The house hover box, the same one every other reference-range bar in
      // the app shows: Current over Previous, following the pointer, so a
      // practitioner comparing two draws can read both numbers off the drop
      // (Maryann, 2026-10-15). These bars had it disabled, which made them
      // the only charts in the report that answered nothing on hover.
      //
      // The series y value is a NORMALIZED position, not the result, so the
      // formatter reports the real numbers from the props rather than
      // anything on the point. `followPointer` is what makes it track the
      // drop image, which is a data label rather than a marker.
      tooltip: {
        followPointer: true,
        followTouchMove: true,
        padding: 5,
        useHTML: true,
        formatter: function () {
          const current = format(value);
          const previous = previousValue === undefined ? 'N/A' : format(previousValue);
          return currentPreviousTooltip(current, previous, currentDate, previousDate);
        },
      },
      // Overlay the two series on ONE row so the previous drop sits at the same
      // height as the current one, exactly as RDWReferenceRangeChart does.
      plotOptions: { bar: { grouping: false, pointPlacement: 0 } },
      series: [
        {
          type: 'bar',
          color: 'rgba(255,255,255,0)',
          borderWidth: 0,
          pointWidth: 10,
          data: [normValue],
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            useHTML: true,
            verticalAlign: 'middle',
            x: offset,
            ...BLOOD_DROP_EDGE_OPTIONS,
            format: bloodDropLabel(bloodDrop),
          },
        } as Highcharts.SeriesBarOptions,
        ...(normPrevious === undefined
          ? []
          : [
              {
                type: 'bar',
                color: 'rgba(255,255,255,0)',
                borderWidth: 0,
                pointWidth: 10,
                data: [normPrevious],
                dataLabels: {
                  enabled: true,
                  allowOverlap: true,
                  useHTML: true,
                  verticalAlign: 'middle',
                  x: offset,
                  ...BLOOD_DROP_EDGE_OPTIONS,
                  format: bloodDropLabel(bloodDropPrevious),
                },
              } as Highcharts.SeriesBarOptions,
            ]),
      ],
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
    };

    return (
      <HighchartsReact
        options={options}
        highcharts={Highcharts}
        callback={reflowSoon}
        containerProps={{ style: { width: '100%', overflow: 'hidden' } }}
      />
    );
  }
}

export default MvxOptimalBar;
